/**
 * Admin → Overview and per-user Usage.
 *
 * Overview: every user on one row — online now (and on which page), connected since,
 * connections / time connected / active days in the window, top pages.
 * Usage: one user — every connection reconstructed from the activity stream (login →
 * last request; a 30-minute silence ends a session), login and failed-login history,
 * pages and features with counts, tickers looked at, and activity per day.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Table, TableBody, TableCell,
  TableHead, TableRow, Select, MenuItem, FormControl, InputLabel, Grid, Tooltip, Button,
} from '@mui/material';
import { Circle, Login, Logout, Timer, Pageview, Warning } from '@mui/icons-material';
import apiService from '../services/api';

const fmtDT = (iso?: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtT = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { timeStyle: 'short' }) : '—');
const mins = (m?: number) => (m == null ? '—' : m < 60 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1)} h`);

function WindowSelect({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel>Window</InputLabel>
      <Select value={days} label="Window" onChange={e => onChange(Number(e.target.value))}>
        <MenuItem value={1}>Today</MenuItem>
        <MenuItem value={7}>Last 7 days</MenuItem>
        <MenuItem value={30}>Last 30 days</MenuItem>
        <MenuItem value={90}>Last 90 days</MenuItem>
      </Select>
    </FormControl>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────

export function AdminOverview({ onSelectUser }: { onSelectUser: (id: number) => void }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setData(await apiService.adminOverview(days)); setError(null); }
    catch (e: any) { setError(e?.response?.data?.detail || e.message); }
  }, [days]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  const online = data.users.filter((u: any) => u.online).length;
  return (
    <Card><CardContent sx={{ p: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, flexWrap: 'wrap' }}>
        <Chip icon={<Circle sx={{ fontSize: 10, color: 'success.main !important' }} />} label={`${online} online now`} size="small" />
        <Typography variant="caption" color="text.secondary">online = a request in the last {data.online_window_minutes} min · refreshes every minute</Typography>
        <Box sx={{ flex: 1 }} />
        <WindowSelect days={days} onChange={setDays} />
      </Box>
      <Table size="small">
        <TableHead><TableRow>
          {['User', 'Now', 'Connected since', 'Last login', 'Connections', 'Time connected', 'Active days', 'Page views', 'Failed logins', 'Top pages'].map(h => (
            <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
          ))}
        </TableRow></TableHead>
        <TableBody>
          {data.users.map((u: any) => (
            <TableRow key={u.id} hover sx={{ cursor: 'pointer' }} onClick={() => onSelectUser(u.id)}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Circle sx={{ fontSize: 10, color: u.online ? 'success.main' : 'text.disabled' }} />
                  <Box component="span" sx={{ fontWeight: 600 }}>{u.username}</Box>
                  {u.is_admin && <Chip size="small" label="admin" color="primary" sx={{ height: 17, fontSize: '0.6rem' }} />}
                  {!u.is_active && <Chip size="small" label="disabled" color="error" sx={{ height: 17, fontSize: '0.6rem' }} />}
                </Box>
              </TableCell>
              <TableCell sx={{ fontSize: '0.78rem' }}>
                {u.online ? <Chip size="small" color="success" variant="outlined" icon={<Pageview sx={{ fontSize: 12 }} />} label={u.current_page || 'online'} sx={{ height: 20, fontSize: '0.65rem' }} />
                  : <Typography variant="caption" color="text.disabled">seen {u.last_seen ? fmtDT(u.last_seen) : 'never'}</Typography>}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>{u.connected_since ? fmtDT(u.connected_since) : '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>{fmtDT(u.last_login)}</TableCell>
              <TableCell>{u.sessions}</TableCell>
              <TableCell>{mins(u.minutes_connected)}</TableCell>
              <TableCell>{u.active_days}</TableCell>
              <TableCell>{u.page_views}</TableCell>
              <TableCell>{u.failed_logins > 0 ? <Chip size="small" color="warning" icon={<Warning sx={{ fontSize: 12 }} />} label={u.failed_logins} sx={{ height: 18, fontSize: '0.65rem' }} /> : '0'}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {u.top_pages.map(([p, n]: [string, number]) => (
                    <Chip key={p} size="small" label={`${p} ×${n}`} sx={{ height: 18, fontSize: '0.62rem' }} />
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.disabled" sx={{ p: 1.5, display: 'block' }}>
        Click a row for the user's connections, pages, features and tickers. Login events are recorded from 2026-09-19; earlier
        connections are reconstructed from activity (a 30-minute silence ends a session).
      </Typography>
    </CardContent></Card>
  );
}

// ── Per-user usage ────────────────────────────────────────────────────────────

export function AdminUserUsage({ userId, users, onChangeUser }: {
  userId: number; users: any[]; onChangeUser: (id: number) => void;
}) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setData(null);
    apiService.adminUserUsage(userId, days).then(setData).catch(e => setError(e?.response?.data?.detail || e.message));
  }, [userId, days]);

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>User</InputLabel>
        <Select value={userId} label="User" onChange={e => onChangeUser(Number(e.target.value))}>
          {users.map(u => <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>)}
        </Select>
      </FormControl>
      <WindowSelect days={days} onChange={setDays} />
    </Box>
  );
  if (error) return <>{header}<Alert severity="error">{error}</Alert></>;
  if (!data) return <>{header}<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box></>;
  const t = data.totals;
  const maxDay = Math.max(1, ...data.by_day.map((d: any) => d.events));

  const Stat = ({ label, value }: { label: string; value: any }) => (
    <Box sx={{ px: 2, py: 1, borderRight: '1px solid', borderColor: 'divider', minWidth: 110 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight="bold">{value}</Typography>
    </Box>
  );

  return (
    <>
      {header}
      <Card sx={{ mb: 2 }}><CardContent sx={{ p: 0, display: 'flex', flexWrap: 'wrap' }}>
        <Stat label="Connections" value={t.sessions} />
        <Stat label="Time connected" value={mins(t.minutes_connected)} />
        <Stat label="Active days" value={t.active_days} />
        <Stat label="Logins" value={t.logins} />
        <Stat label="Failed logins" value={t.failed_logins} />
        <Stat label="Page views" value={t.page_views} />
        <Stat label="Feature calls" value={t.feature_calls} />
        <Stat label="Last seen" value={fmtDT(data.user.last_seen)} />
      </CardContent></Card>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Card><CardContent sx={{ p: 0 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Timer sx={{ fontSize: 16 }} /> Connections ({data.sessions.length})
            </Typography>
            {data.sessions.length === 0 ? <Typography variant="body2" color="text.disabled" sx={{ p: 1.5 }}>No activity in this window.</Typography> : (
              <Table size="small">
                <TableHead><TableRow>
                  {['Started', 'Ended', 'Duration', 'Requests', 'Pages visited (in order)', 'Tickers', 'IP', 'Ended by'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {data.sessions.map((s: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={s.started_by === 'login' ? 'Started with a login' : 'Reconstructed from first activity'}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Login sx={{ fontSize: 12, color: s.started_by === 'login' ? 'success.main' : 'text.disabled' }} />{fmtDT(s.started_at)}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{s.ended_by === 'open' ? <Chip size="small" color="success" label="open" sx={{ height: 18, fontSize: '0.62rem' }} /> : fmtT(s.ended_at)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{mins(s.duration_min)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{s.requests}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>{s.pages.join(' → ') || '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                          {s.tickers.slice(0, 8).map((tk: string) => <Chip key={tk} size="small" label={tk} color="info" sx={{ height: 16, fontSize: '0.58rem' }} />)}
                          {s.tickers.length > 8 && <Typography variant="caption">+{s.tickers.length - 8}</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{s.ip || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem' }}>
                        {s.ended_by === 'logout' ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}><Logout sx={{ fontSize: 12 }} />logout</Box> : s.ended_by}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>

          {(data.logins.length > 0 || data.failed_logins.length > 0) && (
            <Card sx={{ mt: 2 }}><CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Login history</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {[...data.logins.map((l: any) => ({ ...l, ok: true })), ...data.failed_logins.map((l: any) => ({ ...l, ok: false }))]
                  .sort((a, b) => (a.at < b.at ? 1 : -1)).slice(0, 40).map((l: any, i: number) => (
                    <Tooltip key={i} title={`${l.kind} · ${l.ip || 'no ip'}`}>
                      <Chip size="small" color={l.ok ? 'success' : 'error'} variant="outlined"
                        icon={l.ok ? <Login sx={{ fontSize: 12 }} /> : <Warning sx={{ fontSize: 12 }} />}
                        label={fmtDT(l.at)} sx={{ height: 20, fontSize: '0.62rem' }} />
                    </Tooltip>
                  ))}
              </Box>
            </CardContent></Card>
          )}
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ mb: 2 }}><CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Activity per day</Typography>
            {data.by_day.length === 0 ? <Typography variant="body2" color="text.disabled">—</Typography> : (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 70 }}>
                {data.by_day.map((d: any) => (
                  <Tooltip key={d.day} title={`${d.day}: ${d.events} events`}>
                    <Box sx={{ flex: 1, bgcolor: 'primary.main', opacity: 0.8, height: `${Math.max(4, (d.events / maxDay) * 100)}%`, borderRadius: '2px 2px 0 0' }} />
                  </Tooltip>
                ))}
              </Box>
            )}
          </CardContent></Card>

          <Card sx={{ mb: 2 }}><CardContent sx={{ p: 0 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ p: 1.5 }}>Pages opened</Typography>
            <Table size="small"><TableBody>
              {data.pages.length === 0 && <TableRow><TableCell sx={{ color: 'text.disabled' }}>—</TableCell></TableRow>}
              {data.pages.map((p: any) => (
                <TableRow key={p.page} hover>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{p.page}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>×{p.views}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>{fmtDT(p.last_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody></Table>
          </CardContent></Card>

          <Card sx={{ mb: 2 }}><CardContent sx={{ p: 0 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ p: 1.5 }}>Features used (API calls)</Typography>
            <Table size="small"><TableBody>
              {data.features.length === 0 && <TableRow><TableCell sx={{ color: 'text.disabled' }}>—</TableCell></TableRow>}
              {data.features.map((f: any) => (
                <TableRow key={f.feature} hover>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{f.feature}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>×{f.calls}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>{fmtDT(f.last_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody></Table>
          </CardContent></Card>

          <Card><CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Tickers looked at</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {data.tickers.length === 0 && <Typography variant="body2" color="text.disabled">—</Typography>}
              {data.tickers.map((tk: any) => (
                <Tooltip key={tk.ticker} title={`${tk.lookups} lookups · last ${fmtDT(tk.last_at)}`}>
                  <Chip size="small" label={`${tk.ticker} ×${tk.lookups}`} color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                </Tooltip>
              ))}
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>
      <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={() => onChangeUser(userId)}>Refresh</Button>
    </>
  );
}
